/* eslint-disable @next/next/no-img-element */
'use client';

import { useState } from 'react';
import clsx from 'clsx';

export interface EmbeddedVideoProps {
  src: string;
  className?: string;
  thumbnail?: string;
  title?: string;
}

const extractYouTubeVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/,
    /youtube\.com\/v\/([^&?/]+)/,
    /youtube\.com\/shorts\/([^&?/]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }

  return null;
};

const getYouTubeEmbedUrl = (url: string): string => {
  const videoId = extractYouTubeVideoId(url);
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

const getYouTubeThumbnailUrl = (url: string): string | null => {
  const videoId = extractYouTubeVideoId(url);
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  }
  return null;
};

export const EmbeddedVideo: React.FC<EmbeddedVideoProps> = ({
  src,
  className,
  thumbnail,
  title = 'Video player',
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const embedUrl = getYouTubeEmbedUrl(src);
  const thumbnailUrl = thumbnail || getYouTubeThumbnailUrl(src);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  if (isPlaying) {
    // This iframe is eager loaded when playing.
    // This is because user interaction is required to play the video. (Clicking thumbnail to play)
    return (
      <iframe
        loading="eager"
        className={clsx('h-full w-full', className)}
        src={`${embedUrl}?autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      onClick={handlePlay}
      className={clsx(
        'group relative flex h-full w-full items-center justify-center bg-black',
        className
      )}
      aria-label={`Play ${title}`}
    >
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt={title}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      )}
      <div className="absolute inset-0 transition-colors" />
      <div className="relative z-10 flex p-4 items-center justify-center rounded-full bg-primary-darker/40 group-hover:bg-primary-darker/60 shadow-lg transition-transform group-hover:scale-105">
        <svg className="h-12 w-12 text-white translate-x-0.5" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" /></svg>
      </div>
    </button>
  );
};

export default EmbeddedVideo;
